import React, { useState } from 'react'
import styled from 'styled-components'
import Attribute, { Label, Value, Display } from '../Attribute'
import ColorDropdown from '../ColorDropdown'
import ColorVariablePopover from './ColorVariablePopover'
import rgbTo, { ColorFormat } from '../Attribute/components/Color/utils'
import { Color as ColorType, ColorVariable } from 'modules/shares/types'
import Tooltip from 'components/Tooltip'
import copy from 'copy-to-clipboard'
import useHandleCopyValue from 'utils/hooks/useHandleCopyValue'
import { Flex } from 'components/Box'
import DirtyIconTooltip from 'modules/shares/Inspector/Sidebar/components/Style/DirtyIconTooltip'

const copyFormat = (format: ColorFormat, colorParams: ColorType) => {
  copy(rgbTo(format, colorParams))
}

interface ColorAttributeProps extends ColorType {
  label?: string
  style?: React.CSSProperties
  colorVariables?: { [key: string]: ColorVariable }
  display?: Display
  fullWidthColor?: boolean
  dirtyAttributes?: { originalValue: ColorType; originalProperty: string }
}

export const ClickableArea = styled(Label)`
  cursor: pointer;
`

export const LabelWithWarning = styled(Label)`
  display: flex;
`

const ColorVariableAttribute: React.FC<
  ColorAttributeProps & {
    onColorFormatChange: (f: ColorFormat) => void
    colorFormat: ColorFormat
  }
> = ({
  red,
  green,
  blue,
  alpha = 1,
  colorVariableID = undefined,
  colorVariables = undefined,
  label = 'Color',
  onColorFormatChange,
  colorFormat,
  display = 'inline',
  fullWidthColor,
  dirtyAttributes,
  ...props
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false)

  const colorParams = { red, green, blue, alpha }

  const colorVariable =
    colorVariables && colorVariableID ? colorVariables[colorVariableID] : null

  const copyValue = `${colorVariable?.name} ${rgbTo(colorFormat, colorParams)}`

  const {
    handleCopyValue,
    copyTooltipText,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipVisible,
  } = useHandleCopyValue(copyValue)

  return (
    <Attribute
      display={display}
      noCopy
      showIcon={false}
      dropdownVisible={dropdownVisible}
      copyValue={copyValue}
      {...props}>
      <Tooltip
        style={{ width: '33%' }}
        placement="left"
        spacing="10px"
        visible={copyTooltipVisible}
        content={copyTooltipText}>
        <ClickableArea
          onClick={handleCopyValue}
          onMouseEnter={handleEnterCopiableArea}
          onMouseLeave={handleLeaveCopiableArea}>
          <Flex alignItems="center">
            {/* the extra handleEnterCopiableArea is to make sure the copy tooltip is triggered after leaving the dirty icon tooltip */}
            <div
              aria-hidden
              onMouseEnter={
                dirtyAttributes ? handleEnterCopiableArea : undefined
              }>
              {label}
            </div>
            {dirtyAttributes && (
              <DirtyIconTooltip
                originalProperty={dirtyAttributes.originalProperty}
                originalValue={rgbTo(
                  colorFormat,
                  dirtyAttributes.originalValue
                )}
                preventLabelTooltip={handleLeaveCopiableArea}
              />
            )}
          </Flex>
        </ClickableArea>
      </Tooltip>
      <Value
        label={label}
        valueString={copyValue}
        style={fullWidthColor ? { maxWidth: '100%' } : undefined}>
        {colorVariable && (
          <ColorVariablePopover
            red={red}
            green={green}
            blue={blue}
            alpha={alpha}
            onColorFormatChange={onColorFormatChange}
            colorFormat={colorFormat}
            copyValue={copyValue}
            colorVariable={colorVariable}
            dropdown={
              <ColorDropdown
                dropdownOpen={setDropdownVisible}
                onChange={newFormat => {
                  copyFormat(newFormat, colorParams)
                  onColorFormatChange(newFormat)
                }}
              />
            }
          />
        )}
      </Value>
    </Attribute>
  )
}

export default ColorVariableAttribute
